import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { QrCode } from "lucide-react";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      await login(formData.email.trim().toLowerCase(), formData.password);

      toast.success("Tekrar hoş geldiniz!");
      navigate("/admin");
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Giriş başarısız. Bilgilerinizi kontrol edin."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-white flex flex-col">
      <header className="w-full bg-white/70 backdrop-blur border-b border-zinc-100">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-zinc-900 text-white grid place-items-center">
              <QrCode className="w-5 h-5" />
            </div>

            <div className="leading-tight">
              <div className="text-sm font-semibold text-zinc-900">
                megriva QR Menü
              </div>
            </div>
          </Link>

          <nav className="flex shrink-0 items-center gap-2">
            <Link
              to="/"
              className="px-3 py-2 rounded-lg text-sm text-zinc-700 hover:bg-zinc-100 transition"
            >
              Ana Sayfa
            </Link>

            <Link
              to="/register"
              className="px-3 py-2 rounded-lg text-sm font-medium bg-zinc-900 text-white hover:opacity-90 transition"
            >
              Kayıt Ol
            </Link>
          </nav>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <QrCode className="w-12 h-12" />
            </div>

            <h1
              className="text-3xl font-bold tracking-tight"
              style={{ fontFamily: "Playfair Display, serif" }}
              data-testid="login-heading"
            >
              Tekrar Hoş Geldiniz
            </h1>

            <p className="text-zinc-600 mt-2">
              Restoran yönetim panelinize giriş yapın.
            </p>
          </div>

          {/* <div className="bg-zinc-50 border border-zinc-100 rounded-lg p-4 text-sm text-zinc-700">
            <p className="font-medium mb-2">
              Demo Hesaplar (örnek kullanım için):
            </p>
            <p>• demo@burger-and-co.com / demo123</p>
            <p>• demo@napoli-pizza.com / demo123</p>
            <p>• demo@morning-brew.com / demo123</p>
          </div> */}

          <form
            onSubmit={handleSubmit}
            className="space-y-6"
            data-testid="login-form"
          >
            <div className="space-y-2">
              <Label htmlFor="email">E-posta Adresi</Label>
              <Input
                id="email"
                type="email"
                placeholder="ornek@restoraniniz.com"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Şifre</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => handleChange("password", e.target.value)}
                required
                data-testid="login-password-input"
              />
            </div>

            <div className="flex justify-end -mt-3">
              <button
                type="button"
                onClick={() => navigate("/forgot-password")}
                className="text-sm font-medium text-orange-500 hover:text-orange-600 hover:underline transition"
              >
                Şifremi unuttum
              </button>
            </div>

            <Button
              type="submit"
              className="w-full bg-zinc-900 hover:bg-zinc-800 h-11 rounded-full transition-all active:scale-95"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
            </Button>
          </form>

          <p className="text-center text-sm text-zinc-600">
            Henüz bir hesabınız yok mu?{" "}
            <Link
              to="/register"
              className="text-orange-500 hover:text-orange-600 font-medium"
              data-testid="login-register-link"
            >
              Hemen kayıt olun
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}